import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';
import { ResponsiveDialog } from '@/dialog/ResponsiveDialog';

export interface AppRouterProps {
  children: ReactNode;
}

/** The portal's tree, handed to the one route below. */
const AppTreeContext = createContext<ReactNode>(null);

const AppShell = () => (
  <>
    <ResponsiveDialog />
    {useContext(AppTreeContext)}
  </>
);

/**
 * A render error belongs to the portal's own error boundary (Sentry's, above
 * the router), not to React Router's default error page: hand it up.
 */
const RethrowRouteError = () => {
  throw useRouteError();
};

const createAppRouter = () =>
  createBrowserRouter([
    { path: '*', element: <AppShell />, ErrorBoundary: RethrowRouteError },
  ]);

/**
 * A portal's router, with the global dialog host mounted inside it.
 *
 * Dialog content opened with `openDialog(...)` renders in `ResponsiveDialog`,
 * not in the page that opened it. When that host sat above the router, any
 * `<Link>`, `useNavigate`, `useLocation` or `useSearchParams` in dialog content
 * threw and the user got the error page. Mounting the host here gives every
 * dialog the router.
 *
 * It is a data router (one splat route rendering the portal's own `<Routes>`
 * tree), so a page can use `useBlocker` to ask before the user leaves it with
 * unsaved changes (`UnsavedChangesProvider`). The portal's routes are still
 * declared with `<Routes>`/`<Route>` and behave exactly as under
 * `BrowserRouter`.
 *
 * The host is still outside `<Routes>`, so `useParams` and relative links
 * resolve against `/` inside a dialog: pass ids and absolute paths in as props.
 */
export const AppRouter = ({ children }: AppRouterProps) => {
  const [router] = useState(createAppRouter);
  return (
    <AppTreeContext.Provider value={children}>
      <RouterProvider router={router} useTransitions={false} />
    </AppTreeContext.Provider>
  );
};
